import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { Check, ArrowRight, AlertTriangle, Shield } from 'lucide-react';

const CHECKLIST_FETE = [
  { cat: 'Autorisations', items: ['Autorisation mairie obtenue', 'Arrêté de voirie si occupation', 'Assurance événement souscrite', 'Déclaration SACEM (si musique)', 'Horaires légaux respectés (22h)'] },
  { cat: 'Logistique & Sécurité', items: ['Plan d\'accès distribué', 'Balisage et fléchage prévu', 'Extincteurs présents', 'Accès PMR', 'Responsable sécurité désigné', 'Liaison avec secours si > 500 pers.'] },
  { cat: 'Restauration', items: ['Stands food organisés', 'Autorisation vente alcool (si nécessaire)', 'Gestion déchets prévue', 'Plan de nettoyage après fête', 'Eau potable accessible'] },
  { cat: 'Animations & Scène', items: ['Scène ou estrade prévue', 'Sono professionnelle', 'Éclairage scène', 'Programme des animations', 'Groupe ou DJ confirmé'] },
  { cat: 'Bénévoles & Équipe', items: ['Bénévoles recrutés et briefés', 'Planning bénévoles établi', 'Référent par zone', 'Communication radio ou appli', 'Merci / retour d\'expérience prévu'] },
];

export default function GuideFeteLocalePage() {
  const [checkItems, setCheckItems] = useState(
    CHECKLIST_FETE.flatMap(c => c.items.map(item => ({ label: item, cat: c.cat, done: false })))
  );
  const progress = Math.round(checkItems.filter(i => i.done).length / checkItems.length * 100);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HERO */}
      <section className="section-py" style={{ background: 'linear-gradient(135deg, #0B1E20, #1a3a3c)', minHeight: '40vh', display: 'flex', alignItems: 'flex-end' }}>
        <div className="container-ose pb-8">
          <nav className="flex items-center gap-2 text-xs mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Link to="/guides" style={{ color: '#1F9D7A' }}>Guides</Link><span>/</span>
            <span style={{ color: '#FFF' }}>Association & Fête locale</span>
          </nav>
          <span className="inline-block w-fit px-4 py-2 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(31,157,122,0.2)', color: '#1F9D7A', border: '1px solid rgba(31,157,122,0.4)' }}>
            🌿 Guide Association & Fête Locale
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3"
            style={{ color: '#FFF', letterSpacing: '-0.03em', lineHeight: '1.1' }}>
            Organiser une fête locale ou un événement associatif
          </h1>
          <p className="text-sm mb-6 max-w-xl" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.75' }}>
            Sécurité, autorisations, logistique, animations et gestion des bénévoles.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#checklist" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#1F9D7A', color: '#FFF' }}>
              <Check size={15} /> Checklist complète
            </a>
            <Link to="/creer-evenement" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'rgba(255,255,255,0.1)', color: '#FFF', border: '1px solid rgba(255,255,255,0.2)' }}>
              Devis association -15% <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROGRESSION */}
      <div style={{ background: '#0B1E20', borderBottom: '1px solid rgba(31,157,122,0.2)' }}>
        <div className="container-ose py-3 flex items-center gap-4">
          <p className="text-xs font-semibold" style={{ color: '#1F9D7A' }}>{progress}% organisé</p>
          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div className="h-full rounded-full" style={{ width: `${progress}%`, background: '#1F9D7A' }} />
          </div>
        </div>
      </div>

      {/* SÉCURITÉ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="flex items-center gap-3 mb-6">
            <Shield size={22} style={{ color: '#1F9D7A' }} />
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>Sécurité & Réglementation</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              '📋 Autorisation mairie obligatoire pour les rassemblements de plus de 50 personnes',
              '🔊 Déclaration SACEM si diffusion de musique amplifiée',
              '🍺 Licence temporaire débit de boissons si vente alcool',
              '🚒 Liaison pompiers obligatoire au-delà de 1500 personnes',
              '♿ Accessibilité PMR pour tout événement recevant du public',
              '🌙 Respecter les horaires légaux : fin des nuisances sonores à 22h (selon arrêté)',
            ].map((item, i) => (
              <div key={i} className="p-3 rounded-xl text-xs flex items-start gap-2"
                style={{ background: 'rgba(31,157,122,0.04)', border: '1px solid rgba(31,157,122,0.15)' }}>
                <span style={{ color: 'var(--ink)', lineHeight: '1.6' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section id="checklist" className="section-py" style={{ background: 'var(--bg)' }}>
        <div className="container-ose">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>Checklist organisation complète</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CHECKLIST_FETE.map(cat => {
              const catItems = checkItems.filter(i => i.cat === cat.cat);
              const done = catItems.filter(i => i.done).length;
              return (
                <div key={cat.cat} className="card-premium p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{cat.cat}</p>
                    <span className="text-xs font-bold" style={{ color: '#1F9D7A' }}>{done}/{catItems.length}</span>
                  </div>
                  {catItems.map(item => (
                    <button key={item.label} onClick={() => setCheckItems(prev => prev.map(i => i.label === item.label && i.cat === item.cat ? { ...i, done: !i.done } : i))}
                      className="w-full flex items-start gap-2 p-1.5 rounded-lg text-left mb-1"
                      style={{ background: item.done ? 'rgba(31,157,122,0.06)' : 'transparent', border: 'none', cursor: 'pointer' }}>
                      <div className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ borderColor: item.done ? '#1F9D7A' : 'var(--border)', background: item.done ? '#1F9D7A' : 'transparent' }}>
                        {item.done && <Check size={9} color="white" />}
                      </div>
                      <span className="text-xs" style={{ color: item.done ? 'var(--text-muted)' : 'var(--ink)', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-py" style={{ background: '#0B1E20' }}>
        <div className="container-ose text-center">
          <p className="text-xs font-bold mb-2" style={{ color: '#1F9D7A' }}>Tarifs association -15%</p>
          <h2 className="font-display text-2xl font-bold mb-4" style={{ color: '#FFF' }}>Show Événement partenaire de vos fêtes locales</h2>
          <p className="text-sm mb-8 max-w-lg mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Sono, éclairage, animations, barnum — tout pour votre événement avec tarif association. Rayon 1h30 Figeac.
          </p>
          <Link to="/creer-evenement"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold"
            style={{ background: '#1F9D7A', color: '#FFF' }}>
            Demander un devis association <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
